import React, { useEffect, useState } from "react";
import NavigationBar from "../components/Nav/navigationBar";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays,
  CreditCard,
  DollarSign,
  Info,
  KeyRound,
  ScrollText,
} from "lucide-react";

export default function Profile() {
  const [user, setUser] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [section, setSection] = useState("upcoming"); // "profile", "upcoming", "past"
  const BASE_URL = process.env.REACT_APP_SERVER_BASE_URL;
  useEffect(() => {
    const fetchUser = async () => {
      const res = await fetch(`${BASE_URL}/users/get-current-user.php`, {
        method: "GET",
        credentials: "include",
      });
      const data = await res.json();
      if (data.loggedIn) setUser(data);
    };

    const fetchBookings = async () => {
      const res = await fetch(`${BASE_URL}/users/get-customer-bookings.php`, {
        method: "GET",
        credentials: "include",
      });
      const result = await res.json();
      if (result.success) {
        setBookings(result.data);
      }
    };

    fetchUser();
    fetchBookings();
  }, []);

  const upcoming = bookings.filter(
    (b) => new Date(b.check_in_time) >= new Date()
  );
  const past = bookings.filter((b) => new Date(b.check_out_time) < new Date());
  const getImageForCategory = (categoryId) => {
    // Replace with actual image paths
    const images = {
      1: "../assets/rooms/suite/suite.jpg",
      2: "../assets/rooms/double/double.png",
      3: "../assets/rooms/double/no-ac.jpg",
      5: "../assets/rooms/single/ac.jpeg",
      6: "../assets/rooms/single/no-ac.jpeg",
    };
    return images[categoryId] || "/images/default.jpg";
  };
  return (
    <div className="overflow-x-hidden pt-12">
      <NavigationBar />
      <div className="pt-28 flex min-h-screen bg-gradient-to-br from-white to-orange-50">
        {/* Sidebar */}
        <div className="w-64 p-4 flex flex-col justify-between bg-white/70 backdrop-blur border-r border-orange-200">
          <div>
            <h2 className="text-xl font-bold text-orange-700 mb-4">
              My Account
            </h2>

            <button
              className={`block w-full text-left px-4 py-2 rounded-lg mb-2 hover:bg-orange-100 transition ${
                section === "upcoming" ? "bg-orange-200 font-semibold" : ""
              }`}
              onClick={() => setSection("upcoming")}
            >
              Upcoming Bookings
            </button>

            <button
              className={`block w-full text-left px-4 py-2 rounded-lg mb-2 hover:bg-orange-100 transition ${
                section === "past" ? "bg-orange-200 font-semibold" : ""
              }`}
              onClick={() => setSection("past")}
            >
              Past Bookings
            </button>
          </div>

          {/* Logout Button */}
          <button
            className="flex items-center justify-center gap-2 mt-6 w-full py-2.5 rounded-lg bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold shadow-md hover:from-red-600 hover:to-red-700 active:scale-95 transition-transform"
            onClick={async () => {
              try {
                const res = await fetch(`${BASE_URL}/users/logout.php`, {
                  method: "POST",
                  credentials: "include",
                });
                const data = await res.json();
                if (data.success) {
                  setUser(null);
                  window.location.href = "/login";
                }
              } catch (err) {
                console.error("Logout failed:", err);
              }
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            Logout
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
          {section === "profile" && user && (
            <div className="bg-white shadow-md p-6 rounded-lg border border-orange-200">
              <h2 className="text-2xl font-semibold mb-4 text-orange-600">
                Edit Profile
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  className="border p-2 rounded"
                  defaultValue={user.name}
                />
                <input
                  className="border p-2 rounded"
                  defaultValue={user.email}
                />
                <input
                  className="border p-2 rounded"
                  defaultValue={user.phone_number}
                />
                <input
                  className="border p-2 rounded"
                  defaultValue={user.address}
                />
              </div>
              <button className="mt-4 bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700">
                Save Changes
              </button>
            </div>
          )}

          {section === "upcoming" && (
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-orange-600 border-b pb-2">
                Upcoming Bookings
              </h2>

              {upcoming.length === 0 ? (
                <p className="text-gray-500 italic">No upcoming bookings.</p>
              ) : (
                upcoming.map((b, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col md:flex-row items-start gap-4 bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition"
                  >
                    {/* Left: Room Image */}
                    <div className="w-full md:w-48 h-48 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                      <img
                        src={getImageForCategory(b.category_id)}
                        alt={b.category_name}
                        className="w-full h-full object-cover object-center"
                      />
                    </div>

                    {/* Right: Booking Details */}
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-sm md:text-base">
                      <div>
                        <p className="text-gray-500">Room No</p>
                        <p className="font-semibold">{b.room_no}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Category</p>
                        <p className="font-semibold">{b.category_name}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Purpose</p>
                        <p className="font-medium">{b.purpose_of_visit}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Check-in</p>
                        <p className="font-medium">
                          {new Date(b.check_in_time).toLocaleDateString()}
                        </p>
                      </div>

                      <div>
                        <p className="text-gray-500">Check-out</p>
                        <p className="font-medium">
                          {new Date(b.check_out_time).toLocaleDateString()}
                        </p>
                      </div>

                      <div>
                        <p className="text-gray-500">Amount</p>
                        <p className="font-medium">
                          {b.amount ? `${b.currency} ${b.amount}` : "N/A"}
                        </p>
                      </div>

                      <div>
                        <p className="text-gray-500">Payment Status</p>
                        <p
                          className={`font-semibold ${
                            b.payment_status === "Paid"
                              ? "text-green-600"
                              : "text-yellow-600"
                          }`}
                        >
                          {b.payment_status || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {section === "past" && (
            <div>
              <h2 className="text-2xl font-semibold mb-4 text-orange-600">
                Past Bookings
              </h2>
              {past.length === 0 ? (
                <p>No past bookings.</p>
              ) : (
                past.map((b, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col md:flex-row items-start gap-4 bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition"
                  >
                    {/* Left: Room Image */}
                    <div className="w-full md:w-48 h-48 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
                      <img
                        src={getImageForCategory(b.category_id)}
                        alt={b.category_name}
                        className="w-full h-full object-cover object-center"
                      />
                    </div>

                    {/* Right: Booking Details */}
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 text-sm md:text-base">
                      <div>
                        <p className="text-gray-500">Room No</p>
                        <p className="font-semibold">{b.room_no}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Category</p>
                        <p className="font-semibold">{b.category_name}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Purpose</p>
                        <p className="font-medium">{b.purpose_of_visit}</p>
                      </div>

                      <div>
                        <p className="text-gray-500">Check-in</p>
                        <p className="font-medium">
                          {new Date(b.check_in_time).toLocaleDateString()}
                        </p>
                      </div>

                      <div>
                        <p className="text-gray-500">Check-out</p>
                        <p className="font-medium">
                          {new Date(b.check_out_time).toLocaleDateString()}
                        </p>
                      </div>

                      <div>
                        <p className="text-gray-500">Amount</p>
                        <p className="font-medium">
                          {b.amount ? `${b.currency} ${b.amount}` : "N/A"}
                        </p>
                      </div>

                      <div>
                        <p className="text-gray-500">Payment Status</p>
                        <p
                          className={`font-semibold ${
                            b.payment_status === "Paid"
                              ? "text-green-600"
                              : "text-yellow-600"
                          }`}
                        >
                          {b.payment_status || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
